// src/components/ClientCreateModal.js

import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import api from '../api';

// --- FUNCIONES DE CONVERSIÓN ---
const kgToLbs = (kg) => !kg || isNaN(kg) ? '' : (kg * 2.20462).toFixed(1);
const lbsToKg = (lbs) => !lbs || isNaN(lbs) ? '' : (lbs / 2.20462).toFixed(1);
const cmToFtIn = (cm) => {
  if (!cm || isNaN(cm)) return { ft: '', in: '' };
  const totalInches = cm / 2.54;
  const feet = Math.floor(totalInches / 12);
  const inches = (totalInches % 12).toFixed(1);
  return { ft: feet, in: inches };
};
const ftInToCm = (ft, inches) => {
  const ftNum = parseFloat(ft) || 0;
  const inNum = parseFloat(inches) || 0;
  if (ftNum === 0 && inNum === 0) return '';
  const totalInches = ftNum * 12 + inNum;
  return (totalInches * 2.54).toFixed(1);
};

const ClientCreateModal = ({ onClose, onClientCreated }) => {
  // Estados para datos básicos
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [gender, setGender] = useState('');
  const [dob, setDob] = useState('');
  
  // Estados para unidades y conversión
  const [unitSystem, setUnitSystem] = useState('imperial'); // Por defecto en Imperial
  const [weightKg, setWeightKg] = useState('');
  const [weightLbs, setWeightLbs] = useState('');
  const [heightCm, setHeightCm] = useState('');
  const [heightFt, setHeightFt] = useState('');
  const [heightIn, setHeightIn] = useState('');

  // Estados de UI
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  // --- MANEJADORES DE CAMBIO CON CONVERSIÓN ---
  const handleWeightKgChange = (e) => { const kg = e.target.value; setWeightKg(kg); setWeightLbs(kgToLbs(kg)); };
  const handleWeightLbsChange = (e) => { const lbs = e.target.value; setWeightLbs(lbs); setWeightKg(lbsToKg(lbs)); };
  const handleHeightCmChange = (e) => { const cm = e.target.value; setHeightCm(cm); const { ft, in: inches } = cmToFtIn(cm); setHeightFt(ft); setHeightIn(inches); };
  const handleHeightFtInChange = (e, part) => { let ft = heightFt; let inches = heightIn; if (part === 'ft') { ft = e.target.value; setHeightFt(ft); } else { inches = e.target.value; setHeightIn(inches); } setHeightCm(ftInToCm(ft, inches)); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) { setError('Las contraseñas no coinciden.'); return; }
    
    setLoading(true);
    setError('');

    const totalInches = (parseFloat(heightFt) || 0) * 12 + (parseFloat(heightIn) || 0);
    const newClientData = { 
      name, email, password, role: 'client',
      profile: {
        gender: gender || null,
        date_of_birth: dob || null,
        weight_kg: parseFloat(weightKg) || null,
        weight_lbs: parseFloat(weightLbs) || null,
        height_cm: parseFloat(heightCm) || null,
        height_in: totalInches || null,
      }
    };

    try {
      const response = await api.post('/admin/users', {
        ...newClientData,
        status: 'active',
      });
      toast.success('Cliente creado con exito.');
      onClientCreated(response.data);
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al crear el cliente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/75 backdrop-blur-sm flex justify-center items-center z-50 p-4">
      <div className="glass-panel p-8 rounded-xl shadow-xl w-full max-w-2xl animate-fade-up">
        <h2 className="text-2xl font-bold text-white mb-6">Añadir Nuevo Cliente</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-300">Nombre Completo</label>
              <input type="text" id="name" value={name} onChange={(e) => setName(e.target.value)} required className="mt-1 block w-full p-3 bg-gray-700 text-white font-bold rounded-md border-gray-600 focus:ring-marizan-red focus:border-marizan-red"/>
            </div>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-300">Correo Electrónico</label>
              <input type="email" id="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1 block w-full p-3 bg-gray-700 text-white font-bold rounded-md border-gray-600 focus:ring-marizan-red focus:border-marizan-red"/>
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-300">Contraseña Temporal</label>
              <input type="password" id="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1 block w-full p-3 bg-gray-700 text-white font-bold rounded-md border-gray-600 focus:ring-marizan-red focus:border-marizan-red"/>
            </div>
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-300">Confirmar Contraseña</label>
              <input type="password" id="confirmPassword" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required className="mt-1 block w-full p-3 bg-gray-700 text-white font-bold rounded-md border-gray-600 focus:ring-marizan-red focus:border-marizan-red"/>
            </div>
            <div>
              <label htmlFor="gender" className="block text-sm font-medium text-gray-300">Género</label>
              <select id="gender" value={gender} onChange={(e) => setGender(e.target.value)} className="mt-1 block w-full p-3 bg-gray-700 text-white font-bold rounded-md border-gray-600 focus:ring-marizan-red focus:border-marizan-red">
                <option value="">No especificado</option>
                <option value="Male">Masculino</option>
                <option value="Female">Femenino</option>
              </select>
            </div>
            <div>
              <label htmlFor="dob" className="block text-sm font-medium text-gray-300">Fecha de Nacimiento</label>
              <input type="date" id="dob" value={dob} onChange={(e) => setDob(e.target.value)} className="mt-1 block w-full p-3 bg-gray-700 text-white font-bold rounded-md border-gray-600 focus:ring-marizan-red focus:border-marizan-red"/>
            </div>
          </div>
          <div className="flex justify-center space-x-2 bg-gray-900 p-1 rounded-lg my-6">
            <button type="button" onClick={() => setUnitSystem('metric')} className={`w-full py-2 rounded-md transition-colors ${unitSystem === 'metric' ? 'bg-marizan-red text-white' : 'text-gray-400 hover:bg-gray-700'}`}>Métrico (kg/cm)</button>
            <button type="button" onClick={() => setUnitSystem('imperial')} className={`w-full py-2 rounded-md transition-colors ${unitSystem === 'imperial' ? 'bg-marizan-red text-white' : 'text-gray-400 hover:bg-gray-700'}`}>Imperial (lbs/ft,in)</button>
          </div>
          {unitSystem === 'metric' ? (
            <div className="grid grid-cols-2 gap-6">
              <div><label htmlFor="weight-kg" className="block text-sm font-medium text-gray-300">Peso (kg)</label><input type="number" id="weight-kg" step="0.1" value={weightKg} onChange={handleWeightKgChange} className="mt-1 block w-full p-3 bg-gray-700 text-white font-bold rounded-md border-gray-600 focus:ring-marizan-red focus:border-marizan-red"/></div>
              <div><label htmlFor="height-cm" className="block text-sm font-medium text-gray-300">Altura (cm)</label><input type="number" id="height-cm" step="0.1" value={heightCm} onChange={handleHeightCmChange} className="mt-1 block w-full p-3 bg-gray-700 text-white font-bold rounded-md border-gray-600 focus:ring-marizan-red focus:border-marizan-red"/></div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-6">
              <div><label htmlFor="weight-lbs" className="block text-sm font-medium text-gray-300">Peso (lbs)</label><input type="number" id="weight-lbs" step="0.1" value={weightLbs} onChange={handleWeightLbsChange} className="mt-1 block w-full p-3 bg-gray-700 text-white font-bold rounded-md border-gray-600 focus:ring-marizan-red focus:border-marizan-red"/></div>
              <div><label htmlFor="height-ft" className="block text-sm font-medium text-gray-300">Altura (ft, in)</label><div className="flex space-x-2"><input type="number" id="height-ft" value={heightFt} onChange={(e) => handleHeightFtInChange(e, 'ft')} placeholder="ft" className="mt-1 block w-1/2 p-3 bg-gray-700 text-white font-bold rounded-md border-gray-600 focus:ring-marizan-red focus:border-marizan-red"/><input type="number" id="height-in" step="0.1" value={heightIn} onChange={(e) => handleHeightFtInChange(e, 'in')} placeholder="in" className="mt-1 block w-1/2 p-3 bg-gray-700 text-white font-bold rounded-md border-gray-600 focus:ring-marizan-red focus:border-marizan-red"/></div></div>
            </div>
          )}
          {error && <p className="text-red-300 bg-red-950/40 border border-red-900/60 rounded-lg px-3 py-2 text-sm text-center pt-2">{error}</p>}
          <div className="flex justify-end space-x-4 pt-6">
            <button type="button" onClick={onClose} className="bg-gray-600 hover:bg-gray-500 text-white font-bold py-2 px-4 rounded-lg transition-colors">Cancelar</button>
            <button type="submit" disabled={loading} className="bg-marizan-red hover:bg-red-700 text-white font-bold py-2 px-4 rounded-lg transition-colors disabled:opacity-50">{loading ? 'Creando...' : 'Crear Cliente'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ClientCreateModal;